/**
 * The shareable one-pager: where it lives, and what it is called when it travels
 * (SPEC-005 D-7, D-14).
 *
 * The address is a rule rather than a `media` field, because `catalogue.json` is
 * rewritten wholesale by the content pipeline and a path written into it by the
 * sheet pipeline would not survive the next run. `scripts/content/sheetPath.js`
 * restates it for the build, and `plantSheet.test.js` binds the two.
 *
 * Pure: no fetch, no `navigator`, no URL object. Loading the file is
 * `infrastructure/content/plantSheetFile.js`, and sharing it is
 * `application/pwa/sharePlantSheet.js`.
 */

/**
 * The languages a sheet is rendered in: every language the app ships.
 *
 * Not the languages the PROSE exists in, which is what the plant CARD keys off
 * (SPEC-019 D-1) and is the one place these two artifacts part company. A card
 * is nine parts prose to one part chrome, so a German card would be an English
 * card with a German label on it. A sheet is the other way round — twenty
 * labels, four headings and a strip of care copy — so a German sheet is a German
 * page around whatever prose exists, which is precisely what the app already
 * puts on a German reader's screen (D-14).
 */
export const SHEET_LANGUAGES = ['de', 'en', 'hu'];

const FALLBACK_LANGUAGE = 'en';

/** English for anything unshipped, matching `getCatalogue()` for the same input. */
export function sheetLanguage(languageCode) {
    return SHEET_LANGUAGES.includes(languageCode) ? languageCode : FALLBACK_LANGUAGE;
}

/** Same-origin, so the service worker can cache it and no CORS is involved. */
export function plantSheetPath(plantId, languageCode) {
    return `/assets/content/plants/${plantId}/sheet-${sheetLanguage(languageCode)}-1240.jpg`;
}

/**
 * What the file is called once it leaves the app.
 *
 * The plant's name, not its id: this lands in someone's Photos roll and in a
 * message thread, where `purple-coneflower` reads as a filename and
 * `Purple Coneflower` reads as a plant. The extension has to stay `.jpg` —
 * several share targets decide how to treat the file by looking at it.
 */
export function plantSheetFileName(commonName) {
    const name = String(commonName ?? '').trim() || 'plant';

    return `${name} — plant.habb.me.jpg`;
}
